import React, { useMemo } from "react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { dateFormatter, ratingBarRenderer } from "@/utils/formatters";
import { utcDateFilterParams } from "@/utils/grid";
import Grid from "@/lib/organisms/Grid";
import { useActiveApartmentEntities } from "@/store/slices/apartments";
import { useGetLatestAirbnbReviewsQuery, useUpdateAirbnbReviewsMutation } from "@/store/booking-management-api";
import { useGroupApartmentSync } from "@/hooks/useGroupApartmentSync";
import { ReloadIcon, UpdateIcon } from "@radix-ui/react-icons";
import { Button } from "@/lib/shadcn/ui/button";
import { cn } from "@/lib/shadcn/utils";
import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";

interface AirbnbReviewRow {
    id: number;
    apartment: string;
    numberOfReviews: number;
    averageRating: number;
    cleanliness: number | null;
    accuracy: number | null;
    communication: number | null;
    location: number | null;
    checkIn: number | null;
    value: number | null;
    date: Date;
    updateInProgress: boolean;
}

const AirbnbReviewsPage: React.FC = () => {
    const gridName = "Airbnb Reviews";
    const apartmentEntities = useActiveApartmentEntities();

    const { predicates } = useApartmentFilters();
    const { selectedApartment, selectedGroup, groupOptions, apartmentOptions: syncedApartmentOptions, handleGroupChange, handleApartmentChange } =
        useGroupApartmentSync();

    const { data: reviews, isLoading } = useGetLatestAirbnbReviewsQuery();

    const [updateAirbnbReviews, { isLoading: isUpdating }] = useUpdateAirbnbReviewsMutation();

    const anyUpdateInProgress = useMemo(() => {
        return (isUpdating || reviews?.some((review) => review.updateInProgress)) ?? false;
    }, [isUpdating, reviews]);
    // Enable polling when updates are in progress
    useGetLatestAirbnbReviewsQuery(undefined, {
        pollingInterval: anyUpdateInProgress ? 3000 : 0,
    });

    /**
     * Map backend reviews to grid rows, filtered in series by:
     *  1. the shared `<ApartmentFilterBar />` predicates (via `useApartmentFilters`);
     *  2. the page-local `selectedApartment` dropdown (single-apartment drill-in);
     *  3. the page-local `selectedGroup` dropdown (apartment-group view).
     *
     * (2) and (3) come from `userSettings` via `useGroupApartmentSync` — orthogonal
     * user-settings dimensions, left unchanged (FR-015,
     * apartment-filters-graphs-and-database).
     */
    const rows: AirbnbReviewRow[] = useMemo(() => {
        if (!reviews) return [];
        return reviews
            .filter((review) => {
                const apartment = apartmentEntities[review.apartmentId];
                if (!apartment) {
                    return false;
                }
                if (!predicates.every((predicate) => predicate(apartment))) {
                    return false;
                }
                if (selectedApartment !== 0 && selectedApartment !== review.apartmentId) {
                    return false;
                }
                if (selectedGroup !== 0 && apartment.groupId !== selectedGroup) {
                    return false;
                }
                return true;
            })
            .map((review) => ({
                id: review.id,
                apartment: apartmentEntities[review.apartmentId]?.name || "N/A",
                numberOfReviews: review.numberOfReviews,
                averageRating: review.averageRating,
                cleanliness: review.cleanliness,
                accuracy: review.accuracy,
                communication: review.communication,
                location: review.location,
                checkIn: review.checkIn,
                value: review.value,
                date: new Date(review.date),
                updateInProgress: review.updateInProgress,
            }));
    }, [reviews, apartmentEntities, selectedApartment, selectedGroup, predicates]);

    const numberOfReviewsRow: ColDef<AirbnbReviewRow> = {
            field: "numberOfReviews",
            headerName: "N. Reviews",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 120,
            cellClass: "numberFormat",
        },
        apartmentRow: ColDef<AirbnbReviewRow> = {
            field: "apartment",
            headerName: "Apartment",
            cellDataType: "text",
            filter: "agTextColumnFilter",
            width: 290,
        },
        averageRatingRow: ColDef<AirbnbReviewRow> = {
            field: "averageRating",
            headerName: "Overall Rating",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 140,
            cellRenderer: (params: ICellRendererParams<AirbnbReviewRow>) => {
                return ratingBarRenderer(params, 2);
            },
        },
        cleanlinessRow: ColDef<AirbnbReviewRow> = {
            field: "cleanliness",
            headerName: "Cleanliness",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 140,
            cellRenderer: ratingBarRenderer,
        },
        accuracyRow: ColDef<AirbnbReviewRow> = {
            field: "accuracy",
            headerName: "Accuracy",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 140,
            cellRenderer: ratingBarRenderer,
        },
        communicationRow: ColDef<AirbnbReviewRow> = {
            field: "communication",
            headerName: "Communication",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 140,
            cellRenderer: ratingBarRenderer,
        },
        locationRow: ColDef<AirbnbReviewRow> = {
            field: "location",
            headerName: "Location",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 140,
            cellRenderer: ratingBarRenderer,
        },
        checkInRow: ColDef<AirbnbReviewRow> = {
            field: "checkIn",
            headerName: "Check In",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 140,
            cellRenderer: ratingBarRenderer,
        },
        valueRow: ColDef<AirbnbReviewRow> = {
            field: "value",
            headerName: "Value",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 140,
            cellRenderer: ratingBarRenderer,
        },
        dateRow: ColDef<AirbnbReviewRow> = {
            field: "date",
            headerName: "Last Updated",
            valueFormatter: dateFormatter,
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            width: 180,
            cellRenderer: (params: ICellRendererParams<AirbnbReviewRow>) => {
                const formattedDate = params.valueFormatted || "";
                if (params.data?.updateInProgress) {
                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <UpdateIcon className="animate-spin" />
                            <span>{formattedDate}</span>
                        </div>
                    );
                }
                return formattedDate;
            },
        };

    const columnDefinitions: ColDef[] = [
        apartmentRow,
        numberOfReviewsRow,
        averageRatingRow,
        cleanlinessRow,
        accuracyRow,
        communicationRow,
        locationRow,
        checkInRow,
        valueRow,
        dateRow,
    ];

    const handleUpdateReviews = () => {
        updateAirbnbReviews();
    };

    let updateInProgress = isUpdating || anyUpdateInProgress || isLoading;
    return (
        <div data-testid="airbnb-reviews-page" className="flex flex-col gap-2">
            <ApartmentFilterBar />
            <div className={"flex justify-between mb-4"}>
                <div>
                    <Button disabled={updateInProgress} variant="volt" onClick={handleUpdateReviews}>
                        <ReloadIcon className={cn("mr-2 h-4 w-4", { "animate-spin": updateInProgress })} />
                        {isUpdating ? "Updating..." : "Update"}
                    </Button>
                </div>
                <div className={"flex justify-end gap-1"}>
                    <div className="w-50">
                        <SearchableSelect
                            variant="volt"
                            selectedValue={selectedGroup.toString()}
                            onChange={(value) => handleGroupChange(Number(value))}
                            placeholder="Select Group"
                            options={groupOptions}
                            selectedValuePrefix="Group"
                        />
                    </div>

                    <div className="w-auto">
                        <SearchableSelect
                            variant="volt"
                            selectedValue={selectedApartment.toString()}
                            onChange={(value) => handleApartmentChange(Number(value))}
                            placeholder="Select Apartment"
                            options={syncedApartmentOptions}
                            selectedValuePrefix="Apartment"
                            width={"w-full"}
                        />
                    </div>
                </div>
            </div>
            <Grid<AirbnbReviewRow>
                columnDefs={columnDefinitions}
                gridName={gridName}
                initialRowData={rows}
                buttons={[]}
                dropdownFilters={[]}
                getRowId={(params) => params.data.id.toString()}
                columnsToAggregate={[
                    {
                        colDef: averageRatingRow,
                        aggregateFunction: "avg",
                        formatter: (x) => Number(x).toFixed(2),
                    },
                    {
                        colDef: cleanlinessRow,
                        aggregateFunction: "avg",
                        formatter: (x) => Number(x).toFixed(2),
                    },
                    {
                        colDef: accuracyRow,
                        aggregateFunction: "avg",
                        formatter: (x) => Number(x).toFixed(2),
                    },
                    {
                        colDef: communicationRow,
                        aggregateFunction: "avg",
                        formatter: (x) => Number(x).toFixed(2),
                    },
                    {
                        colDef: locationRow,
                        aggregateFunction: "avg",
                        formatter: (x) => Number(x).toFixed(2),
                    },
                    {
                        colDef: checkInRow,
                        aggregateFunction: "avg",
                        formatter: (x) => Number(x).toFixed(2),
                    },
                    {
                        colDef: valueRow,
                        aggregateFunction: "avg",
                        formatter: (x) => Number(x).toFixed(2),
                    },
                ]}
            />
        </div>
    );
};

export default AirbnbReviewsPage;
